import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faTimesCircle, faCheckCircle, faSearch } from '@fortawesome/free-solid-svg-icons';
import api from '../../config/api';
import './AdminPanel.css';

const FeaturedProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [updateLoading, setUpdateLoading] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [searchTerm, products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Tüm aktif ürünleri getir
      const response = await api.get('/admin/products');
      setProducts(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Ürünler yüklenirken hata oluştu:', err);
      setError('Ürünler yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.');
      setLoading(false);
    }
  };

  const filterProducts = () => {
    if (!searchTerm) {
      setFilteredProducts(products);
      return;
    }

    const filtered = products.filter(
      product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.id.toString().includes(searchTerm)
    );
    setFilteredProducts(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleToggleFeatured = async (productId, currentStatus) => {
    try {
      setUpdateLoading(prev => ({ ...prev, [productId]: true }));

      // API üzerinden ürünün öne çıkan durumunu değiştir
      await api.patch(`/admin/products/${productId}`, {
        is_featured: !currentStatus
      });

      // Ürün listesini güncelle
      setProducts(products.map(product => 
        product.id === productId 
          ? { ...product, is_featured: !currentStatus } 
          : product
      ));

      setSuccessMessage(`"${products.find(p => p.id === productId)?.name}" ürünü ${!currentStatus ? 'öne çıkarıldı' : 'öne çıkarılanlardan kaldırıldı'}.`);
      
      // 3 saniye sonra başarı mesajını temizle
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Ürün güncellenirken hata oluştu:', err);
      setError('Ürün güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
      
      // 3 saniye sonra hata mesajını temizle
      setTimeout(() => {
        setError('');
      }, 3000);
    } finally {
      setUpdateLoading(prev => ({ ...prev, [productId]: false }));
    }
  };

  return (
    <div className="featured-products-section">
      <h3 className="section-title mb-4">
        <FontAwesomeIcon icon={faStar} className="me-2 text-warning" />
        Öne Çıkan Ürünler
      </h3>

      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="search-container">
            <Form.Group className="mb-0 position-relative">
              <Form.Control
                type="text"
                placeholder="Ürün adı veya ID ile ara..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input ps-4"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="position-absolute text-muted"
                style={{ left: '10px', top: '12px' }}
              />
            </Form.Group>
          </div>
          <div>
            <Badge bg="primary" className="p-2">
              Toplam Ürün: {products.length}
            </Badge>
            <Badge bg="success" className="ms-2 p-2">
              Öne Çıkan: {products.filter(p => p.is_featured).length}
            </Badge>
          </div>
        </div>

        {successMessage && (
          <Alert variant="success" className="mt-2">
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mt-2">
            {error}
          </Alert>
        )}
      </div>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Ürünler yükleniyor...</p>
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover className="featured-products-table">
            <thead>
              <tr>
                <th style={{ width: '80px' }}>ID</th>
                <th>Ürün Adı</th>
                <th style={{ width: '150px' }}>Fiyat</th>
                <th style={{ width: '120px' }}>Durum</th>
                <th style={{ width: '150px' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length > 0 ? (
                filteredProducts.map(product => (
                  <tr key={product.id}>
                    <td>{product.id}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        {product.images && (
                          <div 
                            className="product-thumbnail me-2" 
                            style={{ 
                              width: '40px', 
                              height: '40px', 
                              backgroundImage: `url(${product.images})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              borderRadius: '4px'
                            }}
                          />
                        )}
                        <span>{product.name}</span>
                      </div>
                    </td>
                    <td>{product.price?.toFixed(2)} TL</td>
                    <td>
                      {product.is_featured ? (
                        <Badge bg="warning" className="text-dark p-2">
                          <FontAwesomeIcon icon={faStar} className="me-1" />
                          Öne Çıkan
                        </Badge>
                      ) : (
                        <Badge bg="light" text="dark" className="p-2">
                          Normal
                        </Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        variant={product.is_featured ? "outline-danger" : "outline-warning"}
                        size="sm"
                        className="w-100"
                        onClick={() => handleToggleFeatured(product.id, product.is_featured)}
                        disabled={updateLoading[product.id]}
                      >
                        {updateLoading[product.id] ? (
                          <Spinner animation="border" size="sm" />
                        ) : product.is_featured ? (
                          <>
                            <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                            Kaldır
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faStar} className="me-1" />
                            Öne Çıkar
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-4">
                    {searchTerm ? (
                      <>
                        <FontAwesomeIcon icon={faSearch} className="me-2 text-muted" />
                        Arama kriterine uygun ürün bulunamadı.
                      </>
                    ) : (
                      'Listelenecek ürün bulunamadı.'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default FeaturedProducts; 